import React, { useEffect, useState } from 'react';
import { useToastStore, ToastType } from '../../stores/toastStore';
import { useThemeStore } from '../../stores/themeStore';

interface ToastConfig {
  accent: string;
  iconBg: string;
  iconColor: string;
  progressBg: string;
  icon: React.ReactNode;
}

const ToastContainer: React.FC = () => {
  const { toasts, dismissToast } = useToastStore();
  const { theme } = useThemeStore();
  const isLight = theme === 'light';

  const getToastConfig = (type: ToastType): ToastConfig => {
    const configs = {
      success: {
        accent: isLight ? 'bg-emerald-500' : 'bg-emerald-400',
        iconBg: isLight ? 'bg-emerald-50' : 'bg-emerald-500/15',
        iconColor: isLight ? 'text-emerald-600' : 'text-emerald-400',
        progressBg: isLight ? 'bg-emerald-500' : 'bg-emerald-400',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ),
      },
      error: {
        accent: isLight ? 'bg-red-500' : 'bg-red-400',
        iconBg: isLight ? 'bg-red-50' : 'bg-red-500/15',
        iconColor: isLight ? 'text-red-600' : 'text-red-400',
        progressBg: isLight ? 'bg-red-500' : 'bg-red-400',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ),
      },
      warning: {
        accent: isLight ? 'bg-amber-500' : 'bg-amber-400',
        iconBg: isLight ? 'bg-amber-50' : 'bg-amber-500/15',
        iconColor: isLight ? 'text-amber-600' : 'text-amber-400',
        progressBg: isLight ? 'bg-amber-500' : 'bg-amber-400',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        ),
      },
      info: {
        accent: isLight ? 'bg-teal-500' : 'bg-teal-400',
        iconBg: isLight ? 'bg-teal-50' : 'bg-teal-500/15',
        iconColor: isLight ? 'text-teal-600' : 'text-teal-400',
        progressBg: isLight ? 'bg-teal-500' : 'bg-teal-400',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
      },
    };
    return configs[type];
  };

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed top-20 right-4 z-[100] flex flex-col gap-2 pointer-events-none"
      style={{ maxWidth: '380px', width: '100%' }}
      role="region"
      aria-label="操作反馈"
      aria-live="polite"
      aria-relevant="additions removals"
    >
      {toasts.map((toastItem) => {
        const config = getToastConfig(toastItem.type);
        return (
          <ToastItem
            key={toastItem.id}
            toast={toastItem}
            config={config}
            isLight={isLight}
            onDismiss={() => dismissToast(toastItem.id)}
          />
        );
      })}
    </div>
  );
};

interface ToastItemProps {
  toast: {
    id: string;
    type: ToastType;
    title: string;
    message?: string;
    duration?: number;
    exiting?: boolean;
  };
  config: ToastConfig;
  isLight: boolean;
  onDismiss: () => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast: toastItem, config, isLight, onDismiss }) => {
  const [progress, setProgress] = useState(100);
  const duration = toastItem.duration ?? 4000;

  useEffect(() => {
    if (toastItem.exiting) return;

    const startTime = Date.now();
    const frameDuration = 50;
    let rafId: number;

    const tick = () => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);

      if (remaining > 0 && !toastItem.exiting) {
        rafId = window.setTimeout(tick, frameDuration);
      }
    };

    rafId = window.setTimeout(tick, frameDuration);
    return () => clearTimeout(rafId);
  }, [toastItem.id, toastItem.exiting, duration]);

  return (
    <div
      role={toastItem.type === 'error' ? 'alert' : 'status'}
      aria-atomic="true"
      className={`
        pointer-events-auto
        relative overflow-hidden
        rounded-xl shadow-lg
        transition-all duration-300 ease-out
        ${toastItem.exiting
          ? 'opacity-0 translate-x-8 scale-95'
          : 'opacity-100 translate-x-0 scale-100 animate-toast-in'
        }
        ${isLight
          ? 'bg-white/95 backdrop-blur-xl border border-stone-200/80'
          : 'bg-stone-900/95 backdrop-blur-xl border border-stone-700/50'
        }
      `}
    >
      <div className="flex items-start gap-3 p-4">
        <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${config.iconBg} ${config.iconColor}`}>
          {config.icon}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className={`text-sm font-semibold ${isLight ? 'text-stone-800' : 'text-stone-100'}`}>
            {toastItem.title}
          </h4>
          {toastItem.message && (
            <p className={`text-xs mt-0.5 ${isLight ? 'text-stone-500' : 'text-stone-400'}`}>
              {toastItem.message}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label={`关闭${toastItem.title}`}
          className={`flex-shrink-0 p-1 rounded-lg transition-colors ${
            isLight ? 'hover:bg-stone-100 text-stone-400' : 'hover:bg-stone-800 text-stone-500'
          }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className={`h-0.5 ${isLight ? 'bg-stone-100' : 'bg-stone-800'}`}>
        <div
          className={`h-full ${config.progressBg} transition-all duration-100 ease-linear`}
          style={{ width: `${progress}%`, opacity: 0.6 }}
        />
      </div>
    </div>
  );
};

export default ToastContainer;
